/**
 * Il drawer della riga del tabellone (rifatto il 30-09-2026, quarto giro).
 *
 * Enrico: *«se clicco su un carico previsto, nel drawer che si apre ho ancora la
 * visione vecchia… il drawer deve essere più semplice, e dal drawer dovremo
 * poter fare le stesse cose che facciamo dalle domande»*. Quindi ha la forma
 * della scheda della domanda, dall'alto in basso:
 *
 *  1. in cima le due mosse per fare mente locale: «Rivedi la conversazione» e
 *     «Chiedi ad Aida»;
 *  2. **Cliente · Spedizioniere · Commessa**, ciascuno correggibile sul posto (il
 *     cliente dall'anagrafica, lo spedizioniere confermando quello possibile o
 *     scegliendolo, le commesse aggiunte anche dal riferimento del cliente);
 *  3. la domanda aperta di Aida con le stesse risposte rapide della scheda;
 *  4. ritiro · pronto dal · pallet **una volta sola**, poi partenza, carico,
 *     documenti e note in sezioni che si aprono.
 *
 * Ogni correzione passa dagli stessi servizi degli strumenti di Aida in chat
 * (`core.m3.righe_tabellone`): quello che si fa qui e quello che le si chiede
 * sono lo stesso gesto. E ogni correzione dice cosa ha fatto, con «Annulla».
 *
 * Vive qui e non dentro la tab perché le viste sulla stessa riga sono due — il
 * tabellone e il calendario: il calendario apre esattamente questo.
 */

import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Eye,
  FileText,
  Loader2,
  Mail,
  MessagesSquare,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Truck,
  Undo2,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { AvvioChat } from "@/components/m3/AidaSidebar";
import { ChiE } from "@/components/m3/ChiE";
import { CommessaDrawer } from "@/components/m3/CommessaDrawer";
import { ComposizioneCarico, SelettoreCommessa } from "@/components/m3/ComposizioneCarico";
import { ConversazioneDrawer } from "@/components/m3/ConversazioneDrawer";
import { PacchettoDrawer } from "@/components/m3/PacchettoDrawer";
import { TestoAvvisoPannello } from "@/components/m3/TestoAvvisoPannello";
import { type Apribile, RigaInterlocutore, RispondiDomanda } from "@/components/m3/SchedaDomanda";
import { NDP_LABEL, StatoBadge } from "@/components/m3/stato-carico";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useCaricoPrevisto,
  useCercaClienti,
  useCpAggiungiCommessa,
  useCpCliente,
  useCpElimina,
  useCpForzaUscita,
  useCpPartenza,
  useCpPatch,
  useCpTogliCommessa,
  useCpTrasportatore,
  useDomanda,
  useTrasportatori,
} from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type {
  CaricoEliminato,
  CaricoPrevistoDetail,
  CommessaRiga,
  EsitoCorrezione,
  PlicoRiassunto,
  RuoloIndirizzo,
} from "@/types/api";

/** Il messaggio dopo una correzione: cosa è cambiato, cosa non torna, e il ritorno. */
type Avviso = { fatto: string[]; avvisi: string[]; annulla?: () => void };

export function CaricoPrevistoDrawer({
  id,
  onClose,
  onApriChat,
  onApriPlico,
  onEliminato,
}: {
  id: string;
  onClose: () => void;
  onApriChat?: (avvio: AvvioChat) => void;
  /** Apre il pacchetto documenti del carico agganciato (sopra questo drawer). */
  onApriPlico?: (caricoId: string) => void;
  /** Il carico è stato eliminato: chi apre il drawer lo chiude e offre «Annulla». */
  onEliminato?: (e: CaricoEliminato) => void;
}) {
  const { data: det, isLoading } = useCaricoPrevisto(id);
  return (
    <div className="fixed inset-0 top-14 z-40 flex justify-end" role="dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l bg-background shadow-xl">
        {isLoading || !det ? (
          <div className="p-4"><Skeleton className="h-80 w-full" /></div>
        ) : (
          <DrawerCorpo
            det={det}
            onClose={onClose}
            onApriChat={onApriChat}
            onApriPlico={onApriPlico}
            onEliminato={onEliminato}
          />
        )}
      </div>
    </div>
  );
}

function testoPerChat(det: CaricoPrevistoDetail): string {
  const cliente = det.cliente_ignoto ? "la riga senza cliente" : `la riga di ${det.cliente_nome}`;
  const commesse = det.commesse.length ? ` (commessa ${det.commesse.join(", ")})` : "";
  if (det.domanda) return `Su ${cliente}${commesse} ti ho chiesto: ${det.domanda.testo}. Dimmi pure.`;
  return `Sono su ${cliente}${commesse}. Cosa vuoi sapere, o cosa correggo?`;
}

function DrawerCorpo({
  det,
  onClose,
  onApriChat,
  onApriPlico,
  onEliminato,
}: {
  det: CaricoPrevistoDetail;
  onClose: () => void;
  onApriChat?: (avvio: AvvioChat) => void;
  onApriPlico?: (caricoId: string) => void;
  onEliminato?: (e: CaricoEliminato) => void;
}) {
  const [mailAperta, setMailAperta] = useState(false);
  const patch = useCpPatch(det.id);
  const [avviso, setAvviso] = useState<Avviso | null>(null);
  const [aperto, setAperto] = useState<{ tipo: "conversazione" } | Apribile | null>(null);
  const [modifica, setModifica] = useState<"cliente" | "spedizioniere" | "commessa" | null>(null);
  const [chiE, setChiE] = useState<{ email: string; ruolo?: Exclude<RuoloIndirizzo, "sconosciuto"> } | null>(null);
  const { data: domanda } = useDomanda(det.domanda?.id ?? null);
  const attesi = new Set((det.domanda?.campi ?? []).map((c) => c.chiave));

  function esito(e: EsitoCorrezione | null | undefined, annulla?: () => void, ripiego = "Salvato.") {
    setModifica(null);
    setAvviso({ fatto: e?.fatto?.length ? e.fatto : [ripiego], avvisi: e?.avvisi ?? [], annulla });
  }
  const semplice = (m: string) => setAvviso({ fatto: [m], avvisi: [] });
  const sconosciuti = det.interlocutori.filter((i) => i.ruolo === "sconosciuto");

  return (
    <>
      {/* ── testata: chi è la riga, a che punto è, e le due mosse ── */}
      <div className="sticky top-0 z-10 border-b bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate font-semibold" title={det.cliente_nome}>
              {det.cliente_ignoto ? "Cliente da identificare" : det.cliente_nome}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
              <StatoBadge stato={det.stato} />
              <span>NDP {NDP_LABEL[det.ndp_badge] ?? det.ndp_badge}{det.ndp_numero ? ` · ${det.ndp_numero}` : ""}</span>
              {det.avvisato_il && <span>· avvisato il {new Date(det.avvisato_il).toLocaleDateString("it-IT")}</span>}
              {det.genesi !== "ndp" && det.lead_time_giorni != null && (
                <span title="Da quando il cliente è stato avvisato">· aspetta da {det.lead_time_giorni} gg</span>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-muted" aria-label="Chiudi">
            <X className="h-4 w-4" />
          </button>
        </div>
        {(det.ha_conversazione || onApriChat || !det.partito_il) && (
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {/* La mail di avviso dal portale (01-10-2026): anche per le righe nate
                prima, o per rimandarla dopo una correzione. */}
            {!det.partito_il && (
              <button
                type="button"
                onClick={() => setMailAperta(true)}
                className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted"
              >
                <Mail className="h-3.5 w-3.5" /> Prepara la mail
              </button>
            )}
            {det.ha_conversazione && (
              <button
                type="button"
                onClick={() => setAperto({ tipo: "conversazione" })}
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-300 bg-brand-50 px-2.5 py-1.5 text-xs font-semibold text-brand-800 hover:bg-brand-100"
              >
                <MessagesSquare className="h-3.5 w-3.5" /> Rivedi la conversazione
              </button>
            )}
            {onApriChat && (
              <button
                type="button"
                onClick={() =>
                  onApriChat({
                    chiave: `${det.id}-${Date.now()}`,
                    richiestaId: det.domanda?.id,
                    rigaId: det.id,
                    domanda: testoPerChat(det),
                  })
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-brand-800"
              >
                <Sparkles className="h-3.5 w-3.5" /> Chiedi ad Aida
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 space-y-4 p-4 text-sm">
        {avviso && <Resoconto a={avviso} onChiudi={() => setAvviso(null)} />}

        {det.mail_non_vista && (
          <div className="flex items-start gap-2 rounded-md border border-brand-200 bg-brand-50 p-2 text-xs text-brand-900" data-testid="banner-mail-non-vista">
            <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Mail preparata il{" "}
              {det.avviso_preparato_il ? new Date(det.avviso_preparato_il).toLocaleDateString("it-IT") : "—"}:
              Aida non l'ha ancora vista. Se l'hai già mandata, controlla che ci fosse{" "}
              <b>Aida in copia</b>.
            </span>
          </div>
        )}
        <Dialog open={mailAperta} onOpenChange={setMailAperta}>
          <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle>La mail per {det.cliente_nome}</DialogTitle>
            </DialogHeader>
            {mailAperta && <TestoAvvisoPannello caricoId={det.id} haSpedizioniere={!!det.trasportatore} />}
          </DialogContent>
        </Dialog>

        {det.da_verificare && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
            <Sparkles className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">Aida ha letto questa riga da una mail: controlla i dati qui sotto.</span>
            <button
              type="button"
              disabled={patch.isPending}
              onClick={() => patch.mutate({ conferma_lettura: true }, { onSuccess: () => semplice("Lettura confermata.") })}
              className="rounded-md border border-amber-400 bg-white px-2 py-1 font-semibold hover:bg-amber-100 disabled:opacity-50"
            >
              Sì, è giusta
            </button>
          </div>
        )}

        {/* ── Cliente · Spedizioniere · Commessa ── */}
        <div className="grid gap-2 sm:grid-cols-3">
          <CartaCliente det={det} aperta={modifica === "cliente"} onApri={() => setModifica(modifica === "cliente" ? null : "cliente")} onFatto={esito} />
          <CartaSpedizioniere det={det} aperta={modifica === "spedizioniere"} onApri={() => setModifica(modifica === "spedizioniere" ? null : "spedizioniere")} />
          <CartaCommesse
            det={det}
            aperta={modifica === "commessa"}
            onApri={() => setModifica(modifica === "commessa" ? null : "commessa")}
            onGuarda={(n) => setAperto({ tipo: "commessa", numero: n })}
            onFatto={esito}
          />
        </div>
        {modifica === "cliente" && (
          <ScegliCliente det={det} onAnnulla={() => setModifica(null)} onFatto={esito} />
        )}
        {modifica === "spedizioniere" && (
          <ChiRitira det={det} onAnnulla={() => setModifica(null)} onFatto={esito} />
        )}
        {modifica === "commessa" && (
          <AggiungiCommessa det={det} onAnnulla={() => setModifica(null)} onFatto={esito} />
        )}

        {/* ── con chi è la conversazione: dove si risponde «Chi è?» ── */}
        {det.interlocutori.length > 0 && (
          <Sezione
            titolo="Con chi è la conversazione"
            conteggio={det.interlocutori.length}
            nota={sconosciuti.length ? `${sconosciuti.length} da riconoscere` : undefined}
            apertaAllInizio={sconosciuti.length > 0}
          >
            <ul className="divide-y rounded-md border px-2">
              {det.interlocutori.map((i) => (
                <RigaInterlocutore
                  key={i.email}
                  i={i}
                  aperto={chiE?.email === i.email}
                  onApri={() => setChiE(chiE?.email === i.email ? null : { email: i.email })}
                />
              ))}
            </ul>
            {chiE && (
              <ChiE
                email={chiE.email}
                ruoloIniziale={(() => {
                  const r = det.interlocutori.find((i) => i.email === chiE.email)?.ruolo;
                  return r && r !== "sconosciuto" ? r : undefined;
                })()}
                clienteSuggerito={
                  det.cliente_codice && !det.cliente_ignoto
                    ? { codice: det.cliente_codice, nome: det.cliente_nome }
                    : null
                }
                onAnnulla={() => setChiE(null)}
                onFatto={(msg) => {
                  setChiE(null);
                  setAvviso({ fatto: msg.length ? msg : ["Salvato."], avvisi: [] });
                }}
              />
            )}
          </Sezione>
        )}

        {/* ── la domanda aperta di Aida, con le stesse risposte della scheda ── */}
        {det.domanda && (
          <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3">
            <div className="flex items-start gap-2">
              <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
                  Aida {det.domanda.parziale ? "aspetta ancora" : "chiede"} · da {det.domanda.giorni_aperta} gg
                </p>
                <p className="mt-0.5 whitespace-pre-line text-sm text-amber-950">
                  {domanda?.testo_domanda || domanda?.spiegazione || det.domanda.testo}
                </p>
                {det.domanda.campi.length > 0 && (
                  <p className="mt-1 text-[11px] text-amber-800">
                    Scrivi {det.domanda.campi.map((c) => c.label.toLowerCase()).join(" e ")} qui
                    sotto, nel ritiro (evidenziat{det.domanda.campi.length > 1 ? "i" : "o"} in giallo):
                    la domanda si chiude da sé.
                  </p>
                )}
              </div>
            </div>
            {domanda && (
              <div className="mt-1">
                <RispondiDomanda
                  d={domanda}
                  conCampi={false}
                  onApri={(a) => setAperto(a)}
                  onFatto={(m) => semplice(m)}
                />
              </div>
            )}
          </div>
        )}

        {/* ── il ritiro: i tre dati, una volta sola ── */}
        <div>
          <Titolo>Il ritiro</Titolo>
          <div className="space-y-2 rounded-md border bg-card p-2.5">
            <CampoTabellone
              label="Data ritiro"
              tipo="date"
              valore={det.data_ritiro}
              fonte={det.campi_fonte?.data_ritiro}
              atteso={attesi.has("data_ritiro")}
              nota={det.data_ritiro_tipo === "attesa" ? "da confermare" : undefined}
              onSalva={(v) => patch.mutate({ data_ritiro: v }, { onSuccess: () => semplice(v ? "Data di ritiro salvata." : "Data di ritiro tolta.") })}
            />
            <CampoTabellone
              label="Pronto dal"
              tipo="date"
              valore={det.pronto_dal}
              fonte={det.campi_fonte?.pronto_dal}
              atteso={attesi.has("pronto_dal")}
              nota={det.pronto && !det.pronto_dal ? "pronto, senza data" : undefined}
              onSalva={(v) => patch.mutate({ pronto_dal: v }, { onSuccess: () => semplice(v ? "«Pronto dal» salvato." : "«Pronto dal» tolto.") })}
            />
            <CampoTabellone
              label="Pallet"
              tipo="number"
              valore={det.pallet_previsti != null ? String(det.pallet_previsti) : null}
              fonte={det.campi_fonte?.pallet}
              atteso={attesi.has("pallet")}
              nota={
                det.pallet_senza_dato?.length
                  ? `più: ${det.pallet_senza_dato.slice(0, 2).join(", ")}${det.pallet_senza_dato.length > 2 ? "…" : ""}`
                  : undefined
              }
              onSalva={(v) =>
                patch.mutate({ pallet: v === null ? null : Number(v) }, { onSuccess: () => semplice("Pallet salvati.") })
              }
            />
            {/* Le targhe del mezzo (01-10-2026): arrivano per mail e Aida le
                scrive se le trova nel testo; qui si correggono. Vanno anche sulla
                pratica dei documenti, se c'è e non ne ha già una. */}
            {(["targa_motrice", "targa_rimorchio"] as const).map((chiave) => (
              <CampoTabellone
                key={chiave}
                label={chiave === "targa_motrice" ? "Targa motrice" : "Targa rimorchio"}
                tipo="targa"
                valore={det[chiave] ?? null}
                fonte={det.campi_fonte?.[chiave]}
                onSalva={(v) =>
                  patch.mutate(
                    { [chiave]: v },
                    {
                      onSuccess: () => semplice(v ? `Targa ${v} salvata.` : "Targa tolta."),
                      onError: (e) =>
                        setAvviso({
                          fatto: ["Targa non salvata."],
                          avvisi: [(e as Error)?.message ?? "Il server l'ha rifiutata."],
                        }),
                    },
                  )
                }
              />
            ))}
          </div>
        </div>

        {/* ── il resto, a richiesta ── */}
        <Sezione
          titolo="Partenza"
          nota={
            det.partito_il
              ? `uscito il ${new Date(det.partito_il).toLocaleDateString("it-IT")}`
              : det.data_partenza_prevista
                ? `prevista il ${new Date(det.data_partenza_prevista).toLocaleDateString("it-IT")}`
                : undefined
          }
        >
          <Partenza det={det} onFatto={semplice} />
        </Sezione>

        <Sezione
          titolo="Carico"
          nota={det.composizione_modificabile ? "cosa parte, riga per riga" : "dalla NDP"}
        >
          <ComposizioneCarico caricoId={det.id} gestioneCommesse={false} />
        </Sezione>

        {det.plico && (
          <Sezione
            titolo="Documenti"
            nota={`${det.plico.documenti_pronti} di ${det.plico.documenti_totali}`}
            apertaAllInizio={det.plico.blocchi.length > 0}
          >
            <RiquadroPlico plico={det.plico} onApri={() => onApriPlico?.(det.plico!.carico_id)} />
          </Sezione>
        )}

        <Sezione titolo="Note" nota={det.note ? "c'è una nota" : undefined} apertaAllInizio={!!det.note}>
          <Note det={det} onFatto={semplice} />
        </Sezione>

        <EliminaCarico det={det} onEliminato={(e) => (onEliminato ? onEliminato(e) : onClose())} />
      </div>

      {aperto?.tipo === "conversazione" && (
        <ConversazioneDrawer
          rigaId={det.id}
          titolo={det.cliente_ignoto ? "Riga senza cliente" : det.cliente_nome}
          onClose={() => setAperto(null)}
        />
      )}
      {aperto?.tipo === "commessa" && (
        <CommessaDrawer numero={aperto.numero} onClose={() => setAperto(null)} />
      )}
      {aperto?.tipo === "avviso" && aperto.id !== det.id && (
        <CaricoPrevistoDrawer id={aperto.id} onClose={() => setAperto(null)} />
      )}
      {aperto?.tipo === "carico" && (
        <PacchettoDrawer caricoId={aperto.id} onClose={() => setAperto(null)} />
      )}
    </>
  );
}

/** Il messaggio dopo una correzione, in cima al corpo: cosa è cambiato, cosa
 *  non torna, e «Annulla» quando il ritorno è un gesto solo. */
function Resoconto({ a, onChiudi }: { a: Avviso; onChiudi: () => void }) {
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-950" role="status">
      <div className="flex items-start gap-2">
        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
        <ul className="min-w-0 flex-1 space-y-0.5">
          {a.fatto.map((f, n) => (
            <li key={n}>{f.charAt(0).toUpperCase() + f.slice(1)}</li>
          ))}
        </ul>
        {a.annulla && (
          <button
            type="button"
            onClick={() => {
              a.annulla?.();
              onChiudi();
            }}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-emerald-400 bg-white px-1.5 py-0.5 text-[11px] font-medium hover:bg-emerald-100"
          >
            <Undo2 className="h-3 w-3" /> Annulla
          </button>
        )}
        <button type="button" onClick={onChiudi} className="shrink-0 rounded p-0.5 hover:bg-emerald-100" aria-label="Chiudi">
          <X className="h-3 w-3" />
        </button>
      </div>
      {a.avvisi.length > 0 && (
        <ul className="mt-1.5 space-y-0.5 border-t border-emerald-200 pt-1.5 text-amber-900">
          {a.avvisi.map((f, n) => (
            <li key={n} className="flex items-start gap-1">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" />
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── le tre carte ─────────────────────────────────────────────────────────────

/** Una carta della testata. Gialla **solo** se c'è qualcosa da fare e si può
 *  farlo cliccando (regola del 30-09: un colore che non si clicca inganna). */
function Carta({
  titolo,
  daFare,
  aperta,
  onApri,
  children,
  etichettaModifica,
}: {
  titolo: string;
  daFare: boolean;
  aperta: boolean;
  onApri: () => void;
  children: React.ReactNode;
  etichettaModifica: string;
}) {
  return (
    <div
      className={cn(
        "relative rounded-md border px-2.5 py-2",
        daFare ? "border-dashed border-amber-300 bg-amber-50/50" : "bg-muted/20",
        aperta && "ring-2 ring-brand-300",
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{titolo}</span>
        <button
          type="button"
          onClick={onApri}
          title={etichettaModifica}
          aria-label={etichettaModifica}
          className={cn(
            "rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground",
            aperta && "bg-muted text-foreground",
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function AzioneCarta({ testo, onClick }: { testo: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 block max-w-full truncate text-left text-[11px] font-semibold text-brand-700 hover:underline"
    >
      {testo} →
    </button>
  );
}

function CartaCliente({
  det,
  aperta,
  onApri,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  aperta: boolean;
  onApri: () => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const [conferma, setConferma] = useState<{ codice: number; nome: string; perche?: string } | null>(null);
  const noto = det.cliente_codice != null && !det.cliente_ignoto;
  const sugg = det.cliente_suggerito;
  return (
    <Carta titolo="Cliente" daFare={!noto} aperta={aperta} onApri={onApri} etichettaModifica="Cambia il cliente">
      {noto ? (
        <>
          <div className="truncate text-xs font-semibold" title={det.cliente_nome}>{det.cliente_nome}</div>
          <div className="text-[10.5px] text-muted-foreground">
            {det.match_incerto ? "riconosciuto da Aida, da confermare" : `codice ${det.cliente_codice}`}
          </div>
        </>
      ) : (
        <>
          <div className="text-xs text-amber-900">
            da identificare
            {!det.cliente_ignoto && det.cliente_nome && (
              <span className="block truncate text-[10.5px] text-muted-foreground" title={det.cliente_nome}>
                letto: «{det.cliente_nome}», non in anagrafica
              </span>
            )}
          </div>
          {sugg ? (
            <>
              <AzioneCarta testo={`È ${sugg.nome}?`} onClick={() => setConferma(sugg)} />
              <div className="truncate text-[10.5px] text-muted-foreground" title={sugg.perche}>{sugg.perche}</div>
            </>
          ) : (
            <AzioneCarta testo="Cercalo in anagrafica" onClick={onApri} />
          )}
        </>
      )}
      {conferma && (
        <ConfermaCliente det={det} scelto={conferma} onChiudi={() => setConferma(null)} onFatto={onFatto} />
      )}
    </Carta>
  );
}

function CartaSpedizioniere({
  det,
  aperta,
  onApri,
}: {
  det: CaricoPrevistoDetail;
  aperta: boolean;
  onApri: () => void;
}) {
  const poss = det.spedizioniere_possibile;
  const noto = !!det.trasportatore || det.ritira_il_cliente;
  return (
    <Carta titolo="Spedizioniere" daFare={!noto && !!poss} aperta={aperta} onApri={onApri} etichettaModifica="Cambia chi ritira">
      {det.ritira_il_cliente ? (
        <div className="text-xs font-semibold">il cliente stesso</div>
      ) : det.trasportatore ? (
        <div className="truncate text-xs font-semibold" title={det.trasportatore}>{det.trasportatore}</div>
      ) : poss ? (
        <>
          <div className="truncate text-xs text-amber-900" title={poss.perche}>possibile: {poss.nome}</div>
          <div className="truncate text-[10.5px] text-muted-foreground" title={poss.perche}>{poss.perche}</div>
          <AzioneCarta testo="Conferma chi ritira" onClick={onApri} />
        </>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">non indicato</div>
          <AzioneCarta testo="Indica chi ritira" onClick={onApri} />
        </>
      )}
    </Carta>
  );
}

function CartaCommesse({
  det,
  aperta,
  onApri,
  onGuarda,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  aperta: boolean;
  onApri: () => void;
  onGuarda: (numero: number) => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const togli = useCpTogliCommessa(det.id);
  const aggiungi = useCpAggiungiCommessa(det.id);
  const [daTogliere, setDaTogliere] = useState<CommessaRiga | null>(null);
  const info = det.commesse_info?.length
    ? det.commesse_info
    : det.commesse.map((n) => ({ numero: n, riferimento_cliente: null, aperta: true, esiste: true, di_altro_cliente: null }));
  const citati = det.riferimenti_map.filter((m) => m.ordine_numero == null).map((m) => m.riferimento);
  return (
    <Carta
      titolo={info.length > 1 ? "Commesse" : "Commessa"}
      daFare={info.length === 0}
      aperta={aperta}
      onApri={onApri}
      etichettaModifica="Aggiungi una commessa"
    >
      {info.length === 0 ? (
        <>
          <div className="text-xs text-amber-900">non indicata</div>
          <AzioneCarta testo="Aggiungila" onClick={onApri} />
        </>
      ) : (
        <ul className="space-y-1">
          {info.map((c) => (
            <li key={c.numero} className="flex items-start gap-1">
              <button
                type="button"
                onClick={() => c.esiste && onGuarda(c.numero)}
                disabled={!c.esiste}
                title={c.esiste ? "Apri la commessa" : undefined}
                className="min-w-0 flex-1 rounded border bg-card px-1.5 py-0.5 text-left hover:border-brand-400 disabled:hover:border-border"
              >
                <span className="flex items-center gap-1 text-xs font-semibold tabular-nums">
                  {c.esiste && <Eye className="h-3 w-3 shrink-0 text-muted-foreground" />}
                  {c.numero}
                  {det.commesse_parziali.includes(c.numero) && (
                    <span className="text-[9.5px] font-medium text-muted-foreground">stralcio</span>
                  )}
                  {!c.aperta && c.esiste && (
                    <span className="rounded bg-muted px-1 text-[9.5px] font-medium text-muted-foreground" title="Non è più nell'export ordini: già spedita o chiusa">
                      chiusa
                    </span>
                  )}
                </span>
                {c.riferimento_cliente && (
                  <span className="block truncate text-[10.5px] text-muted-foreground" title={`Per il cliente: ${c.riferimento_cliente}`}>
                    per il cliente: {c.riferimento_cliente}
                  </span>
                )}
                {!c.esiste && (
                  <span className="block text-[10.5px] text-amber-800">non è una commessa: forse è un numero del cliente</span>
                )}
                {c.di_altro_cliente && (
                  <span className="flex items-center gap-0.5 text-[10.5px] text-amber-800">
                    <AlertTriangle className="h-3 w-3 shrink-0" /> è di {c.di_altro_cliente}
                  </span>
                )}
              </button>
              {(det.composizione_modificabile || !c.esiste) && (
                <button
                  type="button"
                  onClick={() => setDaTogliere(c)}
                  title={c.esiste ? "Togli la commessa dalla riga" : "Spostalo fra i riferimenti del cliente"}
                  aria-label={`Togli ${c.numero}`}
                  className="mt-0.5 rounded p-0.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {citati.length > 0 && (
        <div className="mt-1 text-[10.5px] text-muted-foreground">
          citati nelle mail: <span className="text-foreground/80">{citati.join(" · ")}</span>
        </div>
      )}
      {daTogliere && (
        <DialogSemplice
          titolo={daTogliere.esiste ? `Togli la commessa ${daTogliere.numero}?` : `${daTogliere.numero} non è una commessa`}
          testo={
            daTogliere.esiste
              ? "Le sue righe escono dal carico. Se ti sbagli, dopo c'è «Annulla»."
              : "Lo sposto fra i riferimenti del cliente: resta visibile, e non conta più come commessa."
          }
          conferma={daTogliere.esiste ? "Togli" : "Sposta"}
          inCorso={togli.isPending}
          errore={togli.isError ? (togli.error as Error)?.message : null}
          onAnnulla={() => setDaTogliere(null)}
          onConferma={() => {
            const n = daTogliere.numero;
            const riAggiungibile = daTogliere.esiste;
            togli.mutate(n, {
              onSuccess: (r) => {
                setDaTogliere(null);
                onFatto(
                  r.esito,
                  riAggiungibile
                    ? () => aggiungi.mutate(n, { onSuccess: (r2) => onFatto(r2.esito) })
                    : undefined,
                );
              },
            });
          }}
        />
      )}
    </Carta>
  );
}

// ── le modifiche, sotto le carte ─────────────────────────────────────────────

function Pannello({ titolo, onAnnulla, children }: { titolo: string; onAnnulla: () => void; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-md border border-brand-200 bg-brand-50/30 p-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{titolo}</span>
        <button type="button" onClick={onAnnulla} className="text-[11px] text-muted-foreground hover:text-foreground">
          chiudi
        </button>
      </div>
      {children}
    </div>
  );
}

function CampoCerca({ valore, onChange, segnaposto }: { valore: string; onChange: (v: string) => void; segnaposto: string }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        autoFocus
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        placeholder={segnaposto}
        className="h-8 w-full rounded-md border-2 border-border bg-card pl-7 pr-2 text-xs shadow-sm focus:border-brand-400 focus:outline-none"
      />
    </div>
  );
}

function ScegliCliente({
  det,
  onAnnulla,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  onAnnulla: () => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const [q, setQ] = useState("");
  const { data: trovati, isFetching } = useCercaClienti(q);
  const [scelto, setScelto] = useState<{ codice: number; nome: string } | null>(null);
  return (
    <Pannello titolo="Chi è il cliente?" onAnnulla={onAnnulla}>
      <CampoCerca valore={q} onChange={setQ} segnaposto="Cerca in anagrafica: nome o codice…" />
      {det.cliente_suggerito && !q && (
        <button
          type="button"
          onClick={() => setScelto(det.cliente_suggerito!)}
          className="flex w-full items-center justify-between gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-left text-xs hover:bg-amber-100"
        >
          <span><b>{det.cliente_suggerito.nome}</b> <span className="text-muted-foreground">— {det.cliente_suggerito.perche}</span></span>
          <span className="shrink-0 text-[11px] font-semibold text-brand-700">È lui →</span>
        </button>
      )}
      <ul className="max-h-52 overflow-y-auto">
        {(trovati ?? []).map((c) => (
          <li key={c.codice}>
            <button
              type="button"
              onClick={() => setScelto({ codice: c.codice, nome: c.ragione_sociale })}
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-card"
            >
              <span className="min-w-0 truncate">
                <span className="font-medium">{c.ragione_sociale}</span>
                {c.sul_tabellone && (
                  <span className="text-[10.5px] text-muted-foreground"> · sul tabellone «{c.sul_tabellone}»</span>
                )}
              </span>
              <span className="shrink-0 text-[10.5px] text-muted-foreground">{c.paese ?? ""} · {c.codice}</span>
            </button>
          </li>
        ))}
        {q.trim().length >= 2 && !isFetching && (trovati ?? []).length === 0 && (
          <li className="px-2 py-1 text-[11px] text-muted-foreground">
            Nessun cliente in anagrafica con «{q.trim()}». Se è uno spedizioniere, indicalo nella carta Spedizioniere.
          </li>
        )}
      </ul>
      {scelto && (
        <ConfermaCliente
          det={det}
          scelto={scelto}
          onChiudi={() => setScelto(null)}
          onFatto={(e, a) => {
            setScelto(null);
            onFatto(e, a);
          }}
        />
      )}
    </Pannello>
  );
}

/** Cambiare il cliente è il gesto che insegna di più ad Aida: si conferma. */
function ConfermaCliente({
  det,
  scelto,
  onChiudi,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  scelto: { codice: number; nome: string; perche?: string };
  onChiudi: () => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const salva = useCpCliente(det.id);
  const prima = det.cliente_codice != null && !det.cliente_ignoto ? det.cliente_codice : null;
  return (
    <DialogSemplice
      titolo={`Il cliente è ${scelto.nome}?`}
      testo={
        (prima ? `Al posto di ${det.cliente_nome}. ` : "") +
        (det.email_da_imparare
          ? `Aida ricorderà anche che ${det.email_da_imparare} scrive per ${scelto.nome}: se invece è uno spedizioniere, dillo prima da «Con chi è la conversazione».`
          : "La riga passa a questo cliente.")
      }
      conferma="Conferma"
      inCorso={salva.isPending}
      errore={salva.isError ? (salva.error as Error)?.message : null}
      onAnnulla={onChiudi}
      onConferma={() =>
        salva.mutate(scelto.codice, {
          onSuccess: (r) => {
            onChiudi();
            onFatto(
              r.esito,
              prima != null ? () => salva.mutate(prima, { onSuccess: (r2) => onFatto(r2.esito) }) : undefined,
            );
          },
        })
      }
    />
  );
}

function AggiungiCommessa({
  det,
  onAnnulla,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  onAnnulla: () => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const aggiungi = useCpAggiungiCommessa(det.id);
  const togli = useCpTogliCommessa(det.id);
  const citati = det.riferimenti_map.filter((m) => m.ordine_numero == null).map((m) => m.riferimento);
  const [daRif, setDaRif] = useState<string | null>(null);
  if (!det.composizione_modificabile) {
    return (
      <Pannello titolo="Commesse" onAnnulla={onAnnulla}>
        <p className="text-xs text-muted-foreground">
          Le commesse di questa riga vengono dalla NDP, che dice cosa è uscito: non si cambiano da qui.
        </p>
      </Pannello>
    );
  }
  return (
    <Pannello titolo="Aggiungi una commessa" onAnnulla={onAnnulla}>
      {citati.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-[11px]">
          <span className="text-muted-foreground">Il cliente ha scritto:</span>
          {citati.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setDaRif(daRif === r ? null : r)}
              className={cn(
                "rounded-full border px-2 py-0.5 hover:bg-card",
                daRif === r && "border-brand-400 bg-card font-semibold",
              )}
              title="Cerca la commessa con questo riferimento"
            >
              {r}
            </button>
          ))}
        </div>
      )}
      <SelettoreCommessa
        key={daRif ?? "libera"}
        caricoId={det.id}
        iniziale={daRif ?? ""}
        segnaposto="Numero di commessa, cliente, o il suo riferimento d'ordine («PO 26M…»)…"
        onFatto={onAnnulla}
        onScegli={(c) =>
          aggiungi.mutate(
            { ordine_numero: c.ordine_numero, riferimento: daRif },
            {
              onSuccess: (r) =>
                onFatto(r.esito, () => togli.mutate(c.ordine_numero, { onSuccess: (r2) => onFatto(r2.esito) })),
            },
          )
        }
      />
      {aggiungi.isError && <p className="text-[11px] text-red-600">{(aggiungi.error as Error)?.message}</p>}
    </Pannello>
  );
}

/**
 * Chi ritira: confermare quello possibile, sceglierne uno, «il cliente stesso».
 *
 * La lista propone SEMPRE prima i trasportatori esistenti e «crea nuovo» in
 * fondo: è l'unica cosa che tiene l'anagrafica pulita. Se scrivendo «Bergwerff
 * BV» il portale non proponesse «Bergwerff», l'utente creerebbe il doppione con
 * la coscienza a posto.
 */
function ChiRitira({
  det,
  onAnnulla,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  onAnnulla: () => void;
  onFatto: (e: EsitoCorrezione | null | undefined, annulla?: () => void) => void;
}) {
  const salva = useCpTrasportatore(det.id);
  const poss = det.spedizioniere_possibile;
  const letto = det.trasportatore_letto === "<UNKNOWN>" ? null : det.trasportatore_letto;
  const [q, setQ] = useState(poss?.nome ?? letto ?? "");
  const { data: elenco } = useTrasportatori(q, det.cliente_codice ?? null);
  // com'era prima, per «Annulla»
  const prima = useRef({ id: det.trasportatore_id, stesso: det.ritira_il_cliente });

  function scegli(body: Parameters<typeof salva.mutate>[0]) {
    const p = prima.current;
    salva.mutate(body, {
      onSuccess: (r) =>
        onFatto(r.esito, () =>
          salva.mutate(
            p.stesso ? { cliente_stesso: true } : p.id ? { trasportatore_id: p.id } : {},
            { onSuccess: (r2) => onFatto(r2.esito) },
          ),
        ),
    });
  }

  const trovati = elenco ?? [];
  const uguale = (a: string | null | undefined, b: string | null | undefined) =>
    (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();
  const nomeGiaPreso = trovati.some((t) => uguale(t.nome, q));
  // confermare il possibile: quello dell'anagrafica se c'è, se no si aggiunge
  // col suo indirizzo — così la prossima mail lo riconosce da sola
  const esistente = poss ? trovati.find((t) => uguale(t.nome, q.trim() || poss.nome)) : undefined;

  return (
    <Pannello titolo="Chi ritira?" onAnnulla={onAnnulla}>
      {poss && !det.trasportatore && (
        <button
          type="button"
          disabled={salva.isPending}
          onClick={() =>
            scegli(
              poss.trasportatore_id
                ? { trasportatore_id: poss.trasportatore_id }
                : esistente
                  ? { trasportatore_id: esistente.id }
                  : { nuovo: { nome: q.trim() || poss.nome || "", email: poss.email ? [poss.email] : [] } },
            )
          }
          className="flex w-full items-center justify-between gap-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-left text-xs hover:bg-amber-100 disabled:opacity-50"
        >
          <span className="min-w-0">
            <b>{q.trim() || poss.nome}</b>
            <span className="block truncate text-[10.5px] text-muted-foreground">{poss.perche}</span>
          </span>
          <span className="shrink-0 text-[11px] font-semibold text-brand-700">Sì, è lui →</span>
        </button>
      )}
      <CampoCerca valore={q} onChange={setQ} segnaposto="Cerca o scrivi il nome dello spedizioniere…" />
      <ul className="max-h-44 space-y-0.5 overflow-y-auto">
        {trovati.map((t) => (
          <li key={t.id}>
            <button
              type="button"
              disabled={salva.isPending}
              onClick={() =>
                scegli(
                  // Se Aida aveva letto un nome diverso, questa scelta dice «è un
                  // altro modo di scrivere lui»: il nome diventa un alias.
                  letto && letto.trim().toLowerCase() !== t.nome.trim().toLowerCase()
                    ? { alias_per: t.id }
                    : { trasportatore_id: t.id },
                )
              }
              className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-card"
            >
              <span className="truncate font-medium">{t.nome}</span>
              {t.usi_cliente > 0 && (
                <span className="shrink-0 text-[10px] text-muted-foreground">già {t.usi_cliente}× con questo cliente</span>
              )}
            </button>
          </li>
        ))}
        {trovati.length === 0 && (
          <li className="px-2 py-1 text-[11px] text-muted-foreground">
            {q.trim() ? "Nessuno spedizioniere conosciuto con questo nome." : "Anagrafica vuota."}
          </li>
        )}
      </ul>
      <div className="flex flex-wrap items-center gap-2 border-t pt-2">
        <button
          type="button"
          disabled={salva.isPending || det.ritira_il_cliente}
          onClick={() => scegli({ cliente_stesso: true })}
          className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-50"
        >
          <Truck className="h-3 w-3" /> Il cliente stesso
        </button>
        <button
          type="button"
          disabled={!q.trim() || nomeGiaPreso || salva.isPending}
          onClick={() => scegli({ nuovo: { nome: q.trim(), email: poss?.email ? [poss.email] : [] } })}
          className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> Aggiungi «{q.trim() || "…"}»
        </button>
        {(det.trasportatore || det.ritira_il_cliente || letto) && (
          <button
            type="button"
            disabled={salva.isPending}
            onClick={() => scegli({})}
            className="text-[11px] text-muted-foreground underline underline-offset-2 hover:text-red-600"
          >
            {letto && !det.trasportatore ? "non è uno spedizioniere" : "togli"}
          </button>
        )}
      </div>
      {salva.isError && <p className="text-[11px] text-red-600">{(salva.error as Error)?.message ?? "Non riuscito."}</p>}
    </Pannello>
  );
}

function DialogSemplice({
  titolo,
  testo,
  conferma,
  inCorso,
  errore,
  onAnnulla,
  onConferma,
}: {
  titolo: string;
  testo: string;
  conferma: string;
  inCorso: boolean;
  errore: string | null;
  onAnnulla: () => void;
  onConferma: () => void;
}) {
  return (
    <Dialog open onOpenChange={(v) => !v && onAnnulla()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titolo}</DialogTitle>
          <DialogDescription>{testo}</DialogDescription>
        </DialogHeader>
        {errore && <p className="text-xs text-red-600">{errore}</p>}
        <DialogFooter className="gap-2">
          <button type="button" onClick={onAnnulla} className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted">
            Annulla
          </button>
          <button
            type="button"
            disabled={inCorso}
            onClick={onConferma}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
          >
            {conferma}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── le sezioni che si aprono ─────────────────────────────────────────────────

function Titolo({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{children}</div>;
}

function Sezione({
  titolo,
  nota,
  conteggio,
  apertaAllInizio = false,
  children,
}: {
  titolo: string;
  nota?: string;
  conteggio?: number;
  apertaAllInizio?: boolean;
  children: React.ReactNode;
}) {
  const [aperta, setAperta] = useState(apertaAllInizio);
  return (
    <div className="rounded-md border">
      <button
        type="button"
        onClick={() => setAperta((v) => !v)}
        aria-expanded={aperta}
        className="flex w-full items-center gap-1.5 px-2.5 py-2 text-left hover:bg-muted/40"
      >
        {aperta ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span className="text-xs font-semibold">{titolo}</span>
        {conteggio != null && <span className="text-[11px] text-muted-foreground">({conteggio})</span>}
        {nota && <span className="ml-auto truncate text-[11px] text-muted-foreground">{nota}</span>}
      </button>
      {aperta && <div className="border-t px-2.5 py-2.5">{children}</div>}
    </div>
  );
}

/** Partenza. Qui si PIANIFICA («parte giovedì»); il fatto — «è uscito» — si
 *  registra anche dalla riga, con un click. L'eccezione — uscita senza
 *  documenti — vive qui e solo qui. */
function Partenza({ det, onFatto }: { det: CaricoPrevistoDetail; onFatto: (m: string) => void }) {
  const partenza = useCpPartenza(det.id);
  if (det.partito_il && det.uscita_forzata_motivo) return <UscitaDichiarata det={det} onFatto={onFatto} />;
  if (!det.puo_partire && !det.data_partenza_prevista) return <UscitaSenzaDocumenti det={det} onFatto={onFatto} />;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="date"
        className="h-8 rounded-md border-2 border-border bg-card px-2 text-xs shadow-sm"
        value={det.data_partenza_prevista ?? ""}
        onChange={(e) =>
          partenza.mutate(
            { giorno: e.target.value || null },
            { onSuccess: () => onFatto(e.target.value ? "Giorno di partenza salvato." : "Pianificazione tolta.") },
          )
        }
      />
      {!det.partito_il && (
        <button
          type="button"
          disabled={partenza.isPending}
          onClick={() => partenza.mutate({ uscito: true }, { onSuccess: () => onFatto("Uscita registrata.") })}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted disabled:opacity-50"
        >
          <Truck className="h-3.5 w-3.5" /> Conferma uscita
        </button>
      )}
      {det.partito_il && (
        <span className="text-xs font-medium text-emerald-700">
          Uscito il {new Date(det.partito_il).toLocaleDateString("it-IT")}
        </span>
      )}
      {/* Il DDT letto da Cube (06-10-2026): di oggi lascia la riga «in
          partenza», dei giorni prima l'ha già fatta partire alla sua data. */}
      {det.ddt_numero && (
        <span className="text-xs text-muted-foreground" data-testid="ddt-drawer">
          DDT <span className="font-medium text-foreground">{det.ddt_numero}</span>
          {det.ddt_data && ` del ${new Date(det.ddt_data).toLocaleDateString("it-IT")}`}
          {!det.partito_il && " · domani la riga esce dal tabellone da sola"}
        </span>
      )}
    </div>
  );
}

/**
 * «Elimina carico» (01-10-2026): per il carico creato per errore. Non cancella:
 * **archivia** — sparisce dal tabellone, dal calendario, dai conteggi e dalle
 * mail di Aida, e resta nel DB, recuperabile con «Annulla» subito dopo.
 *
 * In fondo al drawer e dietro una conferma, perché è il gesto più distruttivo
 * della pagina. Sui carichi con un documento (NDP, DDT) o con la merce uscita è
 * disabilitato: lì il carico è vero, e l'errore si corregge invece di eliminare.
 */
function EliminaCarico({
  det,
  onEliminato,
}: {
  det: CaricoPrevistoDetail;
  onEliminato: (e: CaricoEliminato) => void;
}) {
  const elimina = useCpElimina(det.id);
  const [chiedi, setChiedi] = useState(false);
  const [nota, setNota] = useState("");
  const perche = det.non_eliminabile_perche;
  return (
    <div className="border-t pt-3" data-testid="elimina-carico">
      <button
        type="button"
        disabled={!!perche}
        onClick={() => setChiedi(true)}
        title={perche ?? "Il carico è stato creato per errore"}
        className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-border disabled:text-muted-foreground disabled:hover:bg-transparent"
      >
        <Trash2 className="h-3.5 w-3.5" /> Elimina carico
      </button>
      {perche && <p className="mt-1 text-[11px] text-muted-foreground">{perche}</p>}
      <Dialog open={chiedi} onOpenChange={(v) => !elimina.isPending && setChiedi(v)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminare il carico di {det.cliente_nome}?</DialogTitle>
            <DialogDescription>
              Sparisce dal tabellone, dal calendario e dai conteggi, e Aida non lo userà più per
              le mail{det.domanda ? "; la sua domanda aperta si chiude" : ""}. Resta archiviato:
              subito dopo puoi annullare.
            </DialogDescription>
          </DialogHeader>
          <input
            aria-label="Perché lo elimini"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Perché (facoltativo): «doppione della 997», «cliente sbagliato»…"
            className="h-9 w-full rounded-md border bg-card px-2.5 text-sm"
          />
          {elimina.isError && (
            <p className="text-xs text-red-700">{(elimina.error as Error).message}</p>
          )}
          <DialogFooter>
            <Button variant="ghost" disabled={elimina.isPending} onClick={() => setChiedi(false)}>
              No, lascialo
            </Button>
            <Button
              variant="destructive"
              disabled={elimina.isPending}
              onClick={() =>
                elimina.mutate(nota, {
                  onSuccess: (e) => {
                    setChiedi(false);
                    onEliminato(e);
                  },
                })
              }
            >
              {elimina.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
              Sì, elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Note({ det, onFatto }: { det: CaricoPrevistoDetail; onFatto: (m: string) => void }) {
  const patch = useCpPatch(det.id);
  const [note, setNote] = useState(det.note ?? "");
  // La nota può cambiare **dal server** mentre il drawer è aperto (tabella, Aida):
  // si allinea solo se non ci sono modifiche in corso, o si cancellerebbe la
  // frase sotto le dita.
  const notaServer = useRef(det.note ?? "");
  useEffect(() => {
    const arrivata = det.note ?? "";
    if (notaServer.current !== arrivata) {
      setNote((corrente) => (corrente === notaServer.current ? arrivata : corrente));
      notaServer.current = arrivata;
    }
  }, [det.note]);
  const cambiata = note !== (det.note ?? "");
  return (
    <div className="space-y-2">
      <textarea
        className="h-20 w-full rounded-md border-2 border-border bg-card p-2 text-xs focus:border-brand-400 focus:outline-none"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note libere…"
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={patch.isPending || !cambiata}
          onClick={() => patch.mutate({ note }, { onSuccess: () => onFatto("Nota salvata.") })}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-40"
        >
          Salva nota
        </button>
        {det.note && (
          <button
            type="button"
            disabled={patch.isPending}
            onClick={() => {
              setNote("");
              patch.mutate({ note: "" }, { onSuccess: () => onFatto("Nota eliminata.") });
            }}
            className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Elimina nota
          </button>
        )}
      </div>
    </div>
  );
}

const MOTIVI_USCITA = [
  {
    valore: "ndp_non_caricata",
    titolo: "La NDP è stata emessa, ma non è caricata qui",
    aiuto: "Il documento esiste: quando arriveranno i DDT questa riga andrà riconciliata.",
  },
  {
    valore: "senza_ndp",
    titolo: "Spedizione senza NDP",
    aiuto: "Carico piccolo, fatto a mano: senza prelievo da Electric 80 la NDP non esiste.",
  },
] as const;

/**
 * L'uscita dichiarata quando nel portale non c'è nessun documento.
 *
 * Sta nel drawer e non in tabella per una ragione precisa (decisa con Enrico il
 * 29-08): in tabella «Conferma uscita» è il gesto quotidiano, e un'eccezione
 * messa lì accanto con lo stesso peso diventa la scorciatoia comoda — dopo tre
 * settimane le NDP non le carica più nessuno. Qui costa un clic in più, ed è
 * chiusa dietro un secondo clic: si apre solo chi la sta cercando.
 *
 * ⚠️ La data non ha default a oggi. Le righe che questa feature serve a chiudere
 * sono uscite in agosto, e un campo già compilato col giorno del clic si conferma
 * senza guardarlo: lo storico nascerebbe storto proprio dove doveva salvarlo.
 */
function UscitaSenzaDocumenti({
  det,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  onFatto: (m: string) => void;
}) {
  const forza = useCpForzaUscita(det.id);
  const [aperto, setAperto] = useState(false);
  const [giorno, setGiorno] = useState("");
  const [motivo, setMotivo] = useState<string>("");
  const [nota, setNota] = useState("");
  const oggi = new Date().toLocaleDateString("sv-SE");

  if (!aperto) {
    return (
      <div className="space-y-1.5">
        <p className="text-xs text-muted-foreground">
          Serve la NDP (o il DDT) prima di poter pianificare la partenza.
        </p>
        <button
          type="button"
          onClick={() => setAperto(true)}
          className="text-xs font-medium text-brand-700 underline underline-offset-2 hover:text-brand-800"
        >
          La merce è uscita lo stesso →
        </button>
      </div>
    );
  }

  const pronto = giorno !== "" && motivo !== "";
  return (
    <div className="space-y-2.5 rounded-md border border-amber-200 bg-amber-50/60 p-2.5">
      <p className="text-[11px] text-amber-900">
        Registri un'uscita <strong>senza documento</strong>: resterà segnata come
        dichiarata a mano, così quando arriveranno i DDT si sa quali righe ripassare.
      </p>
      <label className="flex items-center gap-2 text-xs">
        <span className="w-24 shrink-0 text-muted-foreground">Uscita il</span>
        <input
          type="date"
          max={oggi}
          value={giorno}
          onChange={(e) => setGiorno(e.target.value)}
          className="h-8 rounded-md border-2 border-border bg-card px-2 text-xs shadow-sm"
        />
      </label>
      <fieldset className="space-y-1">
        <legend className="mb-1 text-[11px] text-muted-foreground">Perché non c'è la NDP</legend>
        {MOTIVI_USCITA.map((m) => (
          <label
            key={m.valore}
            className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 hover:bg-amber-100/60"
          >
            <input
              type="radio"
              name={`motivo-${det.id}`}
              value={m.valore}
              checked={motivo === m.valore}
              onChange={(e) => setMotivo(e.target.value)}
              className="mt-0.5"
            />
            <span className="text-xs">
              {m.titolo}
              <span className="block text-[10.5px] text-muted-foreground">{m.aiuto}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <input
        type="text"
        placeholder="Nota (facoltativa)"
        value={nota}
        onChange={(e) => setNota(e.target.value)}
        className="h-8 w-full rounded-md border-2 border-border bg-card px-2 text-xs shadow-sm"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!pronto || forza.isPending}
          onClick={() =>
            forza.mutate(
              { giorno, motivo, nota: nota.trim() || null },
              {
                onSuccess: () => {
                  setAperto(false);
                  onFatto("Uscita registrata senza documento.");
                },
              },
            )
          }
          className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-card px-2.5 py-1.5 text-xs font-medium hover:bg-amber-100 disabled:opacity-50"
        >
          <Truck className="h-3.5 w-3.5" /> Registra l'uscita
        </button>
        <button
          type="button"
          onClick={() => setAperto(false)}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Annulla
        </button>
        {forza.isError && (
          <span className="text-xs text-red-600">
            {(forza.error as Error)?.message ?? "Non è stato possibile registrare l'uscita."}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Il riepilogo di una riga uscita senza documento: dice cosa è stato dichiarato
 * e lascia il ritorno. Ogni azione che chiude una riga ne ha uno — qui più che
 * altrove, perché questa l'ha chiusa una persona e non un documento.
 */
function UscitaDichiarata({
  det,
  onFatto,
}: {
  det: CaricoPrevistoDetail;
  onFatto: (m: string) => void;
}) {
  const partenza = useCpPartenza(det.id);
  const motivo = MOTIVI_USCITA.find((m) => m.valore === det.uscita_forzata_motivo);
  // Chiusa in Cube senza DDT (06-10-2026): la dichiara il gestionale, non Marina.
  // Niente «Annulla»: al giro successivo la lettura da Cube la richiuderebbe.
  const daCube = det.uscita_forzata_motivo === "evasa_in_cube";
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-emerald-700">
          Uscito il {new Date(det.partito_il!).toLocaleDateString("it-IT")}
        </span>
        <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10.5px] font-semibold text-amber-900">
          {daCube ? "senza DDT" : "senza NDP"}
        </span>
      </div>
      <p className="text-[11px] text-muted-foreground">
        {daCube ? "NDP evasa in Cube senza DDT" : (motivo?.titolo ?? "Uscita dichiarata a mano")}
        {det.uscita_forzata_nota ? ` · ${det.uscita_forzata_nota}` : ""}
      </p>
      {!daCube && <button
        type="button"
        disabled={partenza.isPending}
        onClick={() =>
          partenza.mutate(
            { giorno: null },
            { onSuccess: () => onFatto("Uscita annullata: la riga torna sul tabellone.") },
          )
        }
        className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:opacity-50"
      >
        Annulla questa uscita
      </button>}
    </div>
  );
}

/**
 * Lo stato del pacchetto documenti, dalla riga del tabellone: quanti documenti
 * ci sono e — se è fermo — perché. I motivi sono gli stessi due cancelli della
 * sezione Documenti (data di spedizione, righe che tornano con la nota), letti
 * qui invece che scoprendoli dopo aver aperto.
 */
function RiquadroPlico({ plico, onApri }: { plico: PlicoRiassunto; onApri: () => void }) {
  const fermo = plico.blocchi.length > 0;
  return (
    <div
      className={cn(
        "rounded-md border p-2.5",
        fermo ? "border-amber-300 bg-amber-50" : "border-border bg-card",
      )}
    >
      <div className="flex items-start gap-2">
        {fermo ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-600" />
        ) : (
          <FileText className="mt-0.5 h-4 w-4 flex-none text-brand-700" />
        )}
        <div className="min-w-0 flex-1">
          <div className={cn("text-xs", fermo ? "text-amber-900" : "text-muted-foreground")}>
            {fermo ? (
              <>
                <b>Il plico è fermo.</b> {plico.blocchi.join(" · ")}
              </>
            ) : (
              <>
                <b>
                  {plico.documenti_pronti} document{plico.documenti_pronti === 1 ? "o" : "i"} su{" "}
                  {plico.documenti_totali}
                </b>{" "}
                {plico.documenti_pronti === plico.documenti_totali ? "pronti" : "generati"}
                {plico.data_documenti
                  ? ` · spedizione del ${plico.data_documenti.split("-").reverse().join("/")}`
                  : ""}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onApri}
            className="mt-1.5 inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs font-semibold hover:border-brand-500 hover:text-brand-700"
          >
            <FileText className="h-3 w-3" /> Apri il pacchetto
          </button>
        </div>
      </div>
    </div>
  );
}



/**
 * Campo del tabellone con la sua provenienza accanto. La provenienza è discreta
 * ma sempre presente: è ciò che rende il tabellone affidabile agli occhi di
 * Marina — e dice a noi se Aida sta leggendo bene.
 */
function CampoTabellone({
  label,
  tipo,
  valore,
  fonte,
  nota,
  atteso,
  onSalva,
}: {
  label: string;
  // `targa`: testo libero, maiuscolo. Il server può rifiutarlo (non ha forma di
  // targa), quindi niente spunta ottimista: l'esito lo dice il resoconto.
  tipo: "date" | "number" | "targa" | "testo-derivato";
  valore: string | null;
  fonte?: { fonte: string; il: string; via?: string };
  nota?: string;
  atteso?: boolean;
  onSalva?: (v: string | null) => void;
}) {
  const [bozza, setBozza] = useState(valore ?? "");
  const [salvato, setSalvato] = useState(false);
  useEffect(() => setBozza(valore ?? ""), [valore]);
  useEffect(() => {
    if (!salvato) return;
    const t = setTimeout(() => setSalvato(false), 1800);
    return () => clearTimeout(t);
  }, [salvato]);

  function salva(v: string | null) {
    if ((v ?? "") === (valore ?? "")) return;
    onSalva?.(v);
    if (tipo !== "targa") setSalvato(true);
  }

  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 text-xs text-muted-foreground">
        {label}
        {nota && tipo !== "testo-derivato" && (
          <span className="block text-[10px] text-amber-700">{nota}</span>
        )}
      </span>
      {tipo === "testo-derivato" ? (
        <span className="flex-1 text-sm">
          {valore ?? <span className="text-muted-foreground">—</span>}
          {nota && <span className="ml-1 text-[11px] text-muted-foreground">({nota})</span>}
        </span>
      ) : (
        <span className="flex items-center gap-1">
          <input
            type={tipo === "targa" ? "text" : tipo}
            step={tipo === "number" ? "1" : undefined}
            min={tipo === "number" ? "0" : undefined}
            value={bozza}
            placeholder={tipo === "targa" ? "es. FE826VA" : undefined}
            aria-label={label}
            onChange={(e) => setBozza(tipo === "targa" ? e.target.value.toUpperCase() : e.target.value)}
            onBlur={() => salva(bozza || null)}
            onKeyDown={(e) => {
              if (e.key === "Enter") salva(bozza || null);
            }}
            // Bordo pieno + sfondo chiaro: alla prova non si capiva che fossero
            // campi da compilare. Ambra quando è il dato che Aida aspetta.
            className={cn(
              "h-8 w-44 rounded-md border-2 bg-card px-2 text-xs shadow-sm focus:outline-none focus:ring-2 focus:ring-ring",
              atteso && !valore ? "border-amber-300 bg-amber-50" : "border-border",
            )}
          />
          {valore && (
            <button
              type="button"
              onClick={() => {
                setBozza("");
                salva(null);
              }}
              title="Svuota il campo"
              className="rounded p-0.5 text-muted-foreground hover:text-red-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {salvato && <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
        </span>
      )}
      <Provenienza fonte={fonte} />
    </div>
  );
}

function Provenienza({ fonte }: { fonte?: { fonte: string; il: string; via?: string } }) {
  if (!fonte) return null;
  const ETICHETTA: Record<string, string> = {
    aida: "letto da Aida",
    marina: "detto da te",
    ndp: "dalla NDP",
    m1: "da sistema",
  };
  const quando = new Date(fonte.il).toLocaleDateString("it-IT");
  return (
    <span
      title={`${ETICHETTA[fonte.fonte] ?? fonte.fonte} il ${quando}${fonte.via ? ` (${fonte.via})` : ""}`}
      className={cn(
        "w-20 shrink-0 truncate text-[11px]",
        fonte.fonte === "marina" ? "text-emerald-700" : "text-muted-foreground",
      )}
    >
      {ETICHETTA[fonte.fonte] ?? fonte.fonte}
    </span>
  );
}
